import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { DEFAULT_POLL_INTERVAL_MS } from '~/eds/src/constants/duration';
import { SharedChipProps } from '~/eds/src/types/SharedChipProps';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { ModelProgress } from '../types/model';
import { testIsTerminateProgress } from '../utils/status';
import {
  DEPRECATED_getModelProgressStatusChip,
  getModelProgressStatusChip,
} from '../utils/ui';

interface Props {
  modelId?: Uuid;
}

export const usePollModelProgress = ({
  modelId,
}: Props): {
  isLoading: boolean;
  progress: Nullable<ModelProgress>;
  progressChip: SharedChipProps;
} => {
  const enablePublishIroning = useFlag(FlagType.CustomAiPublishIroning);
  const intl = useIntl();
  const [skipPoll, setSkipPoll] = useState(false);

  const {
    data: progress = null,
    isLoading,
    isUninitialized,
  } = api.endpoints.getPromptModelProgress.useQuery(
    {
      modelId: modelId!,
    },
    {
      pollingInterval: skipPoll ? undefined : DEFAULT_POLL_INTERVAL_MS,
      skip: !modelId,
    },
  );

  // this state management is a workaround for RTKQ not supporting keyed queries for `skip` API
  useEffect(() => {
    if (isUninitialized) {
      setSkipPoll(false);
    } else {
      setSkipPoll(testIsTerminateProgress(progress));
    }
  }, [modelId, progress]);

  const progressChip =
    isLoading || !progress
      ? {
          isLoading: true,
          text: intl.formatMessage({
            id: 'usePollModelProgress.loading',
            defaultMessage: 'Loading…',
            description: 'Loading text for progress chip',
          }),
          width: '100px',
        }
      : enablePublishIroning
        ? getModelProgressStatusChip({
            optimizeState: progress.optimizeState,
            publishStatus: progress.publishStatus,
            docs: {
              processed: progress.docsProcessed,
              inScope: progress.docsInScope,
              failed: progress.docsFailed,
            },
            intl,
          })
        : DEPRECATED_getModelProgressStatusChip({
            optimizeState: progress.optimizeState,
            publishStatus: progress.publishStatus,
            intl,
          });

  return {
    isLoading,
    progress,
    progressChip,
  };
};
